import React, { useCallback, useEffect, useState } from 'react'
import DentalChart from './DentalChart'
import { Accordion } from 'react-bootstrap'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { IDentalChart, ToothCondition } from '@dataTypes/chartingFeature.types'
import { getDentalDataMap } from '@utils/dental-data.utils'
import { initialLowerTeeth, initialUpperTeeth } from '@constants/Charting'
import {
  IPhaseItems,
  IPhases,
  ITreatmentPlan,
  TX_PLAN_STATUS,
  TxPlanStatus,
} from '@dataTypes/treatmentPlan.types'
import { Patient } from '@dataTypes/PatientInfo.types'
import { fetchChartingbyPatientId } from '@store/actions'
import {
  mockDentalCharts,
  mockTreatmentPlans,
} from '@assets/mockdata/PatientTray/mockTreatmentData'
import { shouldUsePatientTrayMockData } from '@utils/patientTrayMocks.util'
import { ReactComponent as ArrowRightMonthIcon } from '@assets/images/Sidepanel/arrow-right-month.svg'
import { ReactComponent as ArrowDownMonthIcon } from '@assets/images/Sidepanel/arrow-down-month.svg'
import { ReactComponent as EyeViewIcon } from '@assets/images/Sidepanel/eye-view.svg'
import { ReactComponent as InTreatmentIcon } from '@assets/images/Icons/In_treatment.svg'
import { getFullName } from '@utils/fullname.util'
import { useTheme } from '@context/ThemeContext'
import { Badge, type BadgeColor, type BadgeMode } from '@enamelai/design-system'
import {
  SkeletonBar,
  SkeletonList,
  SkeletonMonthRow,
} from './SidePanelSkeleton'

type TreatmentProps = {
  patient: Patient | null
}

const TreatmentSectionHeader: React.FC = () => (
  <div className="sp-treatment-section-header">
    <span className="sp-treatment-section-title">Treatment plan overview</span>
  </div>
)

const SKELETON_TEETH = Array.from({ length: 16 }, (_, index) => index)

const DentalChartSkeleton: React.FC = () => (
  <div className="sp-treatment-chart-card" aria-hidden="true">
    <div className="sp-treatment-chart-skeleton">
      {['upper', 'lower'].map((row) => (
        <div key={row} className="sp-treatment-chart-skeleton-teeth">
          {SKELETON_TEETH.map((tooth) => (
            <SkeletonBar key={tooth} height={38} />
          ))}
        </div>
      ))}
      <div className="sp-treatment-chart-skeleton-legend">
        {[52, 52, 52].map((width, index) => (
          <SkeletonBar key={index} width={width} height={10} />
        ))}
      </div>
    </div>
  </div>
)

const formatSurfaceDisplay = (surface?: string | null): string => {
  if (!surface?.trim()) return '-'
  const first = surface.split(',')[0]?.trim()
  if (!first) return '-'
  const slashIdx = first.indexOf('/')
  if (slashIdx === -1) return `${first}/-`
  const tooth = first.slice(0, slashIdx)
  const surf = first.slice(slashIdx + 1)
  return surf ? `${tooth}/${surf}` : `${tooth}/-`
}

const getProcedureCode = (item: IPhaseItems): string =>
  item.procedure_settings?.procedure?.procedure_code ?? '—'

const getProcedureDescription = (item: IPhaseItems): string =>
  item.procedure_settings?.description ??
  item.procedure_settings?.procedure?.description ??
  '—'

const getPhaseStatusColor = (status: TxPlanStatus): BadgeColor => {
  switch (status) {
    case TX_PLAN_STATUS.PROPOSED:
      return 'cyan'
    case TX_PLAN_STATUS.ACCEPTED:
      return 'vivid-green'
    case TX_PLAN_STATUS.COMPLETED:
      return 'pale-green'
    case TX_PLAN_STATUS.REJECTED:
      return 'red'
    default:
      return 'gray'
  }
}

const PhaseProcedureCard: React.FC<{
  item: IPhaseItems
  providerName: string
}> = ({ item, providerName }) => (
  <div className="sp-treatment-procedure-card">
    <div className="sp-treatment-procedure-top">
      <span className="sp-treatment-procedure-code">
        {getProcedureCode(item)}
      </span>
      <span className="sp-treatment-procedure-provider">{providerName}</span>
    </div>
    <div className="sp-treatment-procedure-body">
      <div className="sp-treatment-procedure-tooth">
        <InTreatmentIcon className="sp-treatment-procedure-icon" aria-hidden />
        <span>{formatSurfaceDisplay(item.surface)}</span>
      </div>
      <div className="sp-treatment-procedure-desc">
        {getProcedureDescription(item)}
      </div>
    </div>
  </div>
)

const TreatmentAccordionArrow: React.FC<{ expanded: boolean }> = ({
  expanded,
}) => (
  <span className="sp-treatment-accordion-arrow" aria-hidden>
    {expanded ? (
      <ArrowDownMonthIcon className="sp-treatment-accordion-arrow-icon" />
    ) : (
      <ArrowRightMonthIcon className="sp-treatment-accordion-arrow-icon" />
    )}
  </span>
)

const Treatment: React.FC<TreatmentProps> = ({ patient }) => {
  const dispatch = useAppDispatch()
  const { resolvedTheme } = useTheme()
  const badgeMode: BadgeMode = resolvedTheme === 'dark' ? 'solid' : 'tinted'
  const { refresh } = useAppSelector((state) => state.patient)
  const chartingLoading = useAppSelector((state) => state.charting.loading)
  const dentalChartsRedux = useAppSelector(
    (state) => state.charting.dentalCharts,
  )
  const treatmentPlansRedux = useAppSelector(
    (state) => state.charting.treatmentPlans,
  )
  const providers = useAppSelector((state) => state.provider.data)
  const [mockDentalChartsState, setMockDentalChartsState] = useState<
    IDentalChart[]
  >([])
  const [mockTreatmentPlansState, setMockTreatmentPlansState] = useState<
    ITreatmentPlan[]
  >([])
  const [upperTeeth, setUpperTeeth] = useState(initialUpperTeeth)
  const [lowerTeeth, setLowerTeeth] = useState(initialLowerTeeth)
  const [activePhaseKey, setActivePhaseKey] = useState<string | null>(null)
  const [activePlanKey, setActivePlanKey] = useState<string | null>(null)

  const useMockTreatmentData = shouldUsePatientTrayMockData(patient?.patient_id)
  const dentalCharts = useMockTreatmentData
    ? mockDentalChartsState
    : dentalChartsRedux
  const treatmentPlans = useMockTreatmentData
    ? mockTreatmentPlansState
    : treatmentPlansRedux

  const getProviderName = useCallback(
    (item: IPhaseItems): string => {
      const settings = item.procedure_settings
      const fromSettings =
        settings?.rendering_to_provider ||
        settings?.provider_settings?.rendering_to_provider
      if (fromSettings) return getFullName(fromSettings, true)

      if (item.provider_id) {
        const provider = providers.find(
          (p) => p.provider_id === item.provider_id,
        )
        if (provider) return getFullName(provider, true)
      }

      return '—'
    },
    [providers],
  )

  const getPlanKey = (plan: ITreatmentPlan, planIndex: number): string =>
    plan.tp_id || `plan-${planIndex}`

  const getPhaseKey = (
    phase: IPhases,
    planIndex: number,
    phaseIndex: number,
  ): string => phase.phase_id || `phase-${planIndex}-${phaseIndex}`

  const getMissingTeeth = (
    dentalChart: IDentalChart,
  ): Record<number, ToothCondition[]> => {
    const missingTeeth: Record<number, ToothCondition[]> = {}
    const dentalDataMap = getDentalDataMap(dentalChart.dental_data)

    Object.entries(dentalDataMap).forEach(([toothNumber, toothData]) => {
      const toothNum = parseInt(toothNumber)
      const hasMissing = toothData.some((data) => data.condition === 'missing')

      if (hasMissing) {
        missingTeeth[toothNum] = ['missing'] as ToothCondition[]
      }
    })

    return missingTeeth
  }

  const setMissingTeeth = useCallback(
    (dentalChart: IDentalChart, init: boolean = false): void => {
      if (init) {
        setUpperTeeth(initialUpperTeeth)
        setLowerTeeth(initialLowerTeeth)
      }
      const missingTeeth = getMissingTeeth(dentalChart)
      setUpperTeeth((prevUpperTeeth) =>
        prevUpperTeeth.map((tooth) => ({
          ...tooth,
          status: missingTeeth[tooth.id] ? 'Missing' : tooth.status,
        })),
      )

      setLowerTeeth((prevLowerTeeth) =>
        prevLowerTeeth.map((tooth) => ({
          ...tooth,
          status: missingTeeth[tooth.id] ? 'Missing' : tooth.status,
        })),
      )
    },
    [],
  )

  const getTreatedTeeth = (plans: ITreatmentPlan[]): Record<number, string> => {
    const treatedTeeth: Record<number, string> = {}

    plans.forEach((plan) => {
      plan.phases?.forEach((phase) => {
        if (phase.status === TX_PLAN_STATUS.COMPLETED) {
          phase.items?.forEach((item) => {
            const teethNums = item.surface?.split(',') || []
            for (const tooth of teethNums) {
              const toothNum = parseInt(tooth.split('/')[0])
              treatedTeeth[toothNum] = 'Treated'
            }
          })
        }
      })
    })

    return treatedTeeth
  }

  const setTreatedTeeth = (plans: ITreatmentPlan[]): void => {
    const treatedTeeth = getTreatedTeeth(plans)
    setUpperTeeth((prevUpperTeeth) =>
      prevUpperTeeth.map((tooth) => ({
        ...tooth,
        status: treatedTeeth[tooth.id] ? 'Treated' : tooth.status,
      })),
    )
    setLowerTeeth((prevLowerTeeth) =>
      prevLowerTeeth.map((tooth) => ({
        ...tooth,
        status: treatedTeeth[tooth.id] ? 'Treated' : tooth.status,
      })),
    )
  }

  const getPlannedTeeth = (phase: IPhases): Record<number, string> => {
    const plannedTeeth: Record<number, string> = {}

    if (
      phase.status === TX_PLAN_STATUS.PROPOSED ||
      phase.status === TX_PLAN_STATUS.ACCEPTED
    ) {
      phase.items?.forEach((item) => {
        const teethNums = item.surface?.split(',') || []
        for (const tooth of teethNums) {
          const toothNum = parseInt(tooth.split('/')[0])
          plannedTeeth[toothNum] = 'Planned'
        }
      })
    }

    return plannedTeeth
  }

  const getPlannedTeethfromPlan = (
    plan: ITreatmentPlan,
  ): Record<number, string> => {
    const plannedTeeth: Record<number, string> = {}
    plan.phases?.forEach((phase) => {
      if (
        phase.status === TX_PLAN_STATUS.PROPOSED ||
        phase.status === TX_PLAN_STATUS.ACCEPTED
      ) {
        phase.items?.forEach((item) => {
          const teethNums = item.surface?.split(',') || []
          for (const tooth of teethNums) {
            const toothNum = parseInt(tooth.split('/')[0])
            plannedTeeth[toothNum] = 'Planned'
          }
        })
      }
    })
    return plannedTeeth
  }

  const handlePlanClick = (plan: ITreatmentPlan, planKey: string): void => {
    const plannedTeeth = getPlannedTeethfromPlan(plan)
    if (activePlanKey === planKey) {
      setActivePlanKey(null)
      setActivePhaseKey(null)
      setUpperTeeth((prevUpperTeeth) =>
        prevUpperTeeth.map((tooth) => ({
          ...tooth,
          status: plannedTeeth[tooth.id] ? 'None' : tooth.status,
        })),
      )
      setLowerTeeth((prevLowerTeeth) =>
        prevLowerTeeth.map((tooth) => ({
          ...tooth,
          status: plannedTeeth[tooth.id] ? 'None' : tooth.status,
        })),
      )
      setTreatedTeeth(treatmentPlans)
      if (dentalCharts[0]) {
        setMissingTeeth(dentalCharts[0])
      }
    } else {
      setActivePlanKey(planKey)
      setActivePhaseKey(null)
      if (!plan.phases) return
      for (const phase of plan.phases) {
        if (!phase.phase_id) continue
        if (activePhaseKey === phase.phase_id) {
          const phasePlannedTeeth = getPlannedTeeth(phase)
          setUpperTeeth((prevUpperTeeth) =>
            prevUpperTeeth.map((tooth) => ({
              ...tooth,
              status: phasePlannedTeeth[tooth.id] ? 'Planned' : tooth.status,
            })),
          )
          setLowerTeeth((prevLowerTeeth) =>
            prevLowerTeeth.map((tooth) => ({
              ...tooth,
              status: phasePlannedTeeth[tooth.id] ? 'Planned' : tooth.status,
            })),
          )
          break
        }
      }
    }
  }

  const handlePhaseClick = (phase: IPhases, phaseKey: string): void => {
    const plannedTeeth = getPlannedTeeth(phase)
    if (activePhaseKey === phaseKey) {
      setActivePhaseKey(null)
      if (phase.status === TX_PLAN_STATUS.COMPLETED) return
      setUpperTeeth((prevUpperTeeth) =>
        prevUpperTeeth.map((tooth) => ({
          ...tooth,
          status: plannedTeeth[tooth.id] ? 'None' : tooth.status,
        })),
      )

      setLowerTeeth((prevLowerTeeth) =>
        prevLowerTeeth.map((tooth) => ({
          ...tooth,
          status: plannedTeeth[tooth.id] ? 'None' : tooth.status,
        })),
      )
      setTreatedTeeth(treatmentPlans)
      if (dentalCharts[0]) {
        setMissingTeeth(dentalCharts[0])
      }
    } else {
      setActivePhaseKey(phaseKey)
      if (phase.status === TX_PLAN_STATUS.COMPLETED) return
      setUpperTeeth((prevUpperTeeth) =>
        prevUpperTeeth.map((tooth) => ({
          ...tooth,
          status: plannedTeeth[tooth.id] ? 'Planned' : tooth.status,
        })),
      )

      setLowerTeeth((prevLowerTeeth) =>
        prevLowerTeeth.map((tooth) => ({
          ...tooth,
          status: plannedTeeth[tooth.id] ? 'Planned' : tooth.status,
        })),
      )
    }
  }

  useEffect(() => {
    setActivePlanKey(null)
    setActivePhaseKey(null)
  }, [patient?.patient_id])

  useEffect(() => {
    if (!patient?.patient_id) return

    const useMockData = shouldUsePatientTrayMockData(patient.patient_id)
    if (useMockData) {
      const patientId = patient.patient_id
      setMockDentalChartsState(mockDentalCharts[patientId] || [])
      setMockTreatmentPlansState(mockTreatmentPlans[patientId] || [])
    } else {
      dispatch(fetchChartingbyPatientId(patient.patient_id))
    }
  }, [patient?.patient_id, refresh, dispatch])

  useEffect(() => {
    if (treatmentPlans.length === 0) return

    setActivePlanKey((current) => {
      if (current !== null) return current
      return getPlanKey(treatmentPlans[0], 0)
    })

    setActivePhaseKey((current) => {
      if (current !== null) return current
      const firstPhase = treatmentPlans[0].phases?.[0]
      return firstPhase ? getPhaseKey(firstPhase, 0, 0) : null
    })
  }, [treatmentPlans])

  useEffect(() => {
    if (patient && dentalCharts.length > 0) {
      setMissingTeeth(dentalCharts[0], true)
      setTreatedTeeth(treatmentPlans)
    }
    if (treatmentPlans.length > 0) {
      setTreatedTeeth(treatmentPlans)
    }
  }, [dentalCharts, treatmentPlans])

  const showSkeleton = chartingLoading && !useMockTreatmentData

  return (
    <div className="sp-treatment">
      <TreatmentSectionHeader />
      {showSkeleton ? (
        <DentalChartSkeleton />
      ) : (
        <DentalChart
          upperTeeth={upperTeeth}
          lowerTeeth={[...lowerTeeth].reverse()}
        />
      )}
      <div className="sp-treatment-plans">
        {showSkeleton ? (
          <SkeletonList>
            {['60%', '45%', '55%'].map((width) => (
              <SkeletonMonthRow key={width} width={width} />
            ))}
          </SkeletonList>
        ) : treatmentPlans.length === 0 ? (
          <p className="sp-treatment-empty">No treatment plans available</p>
        ) : (
          <Accordion
            activeKey={activePlanKey}
            onSelect={(eventKey) => {
              const newPlanKey = eventKey === null ? null : (eventKey as string)
              setActivePlanKey(newPlanKey)
              if (newPlanKey !== activePlanKey) {
                setActivePhaseKey(null)
              }
            }}
            className="sp-treatment-plan-accordion"
          >
            {treatmentPlans.map((plan, planIndex) => {
              const planKey = getPlanKey(plan, planIndex)
              const isPlanExpanded = activePlanKey === planKey

              return (
                <Accordion.Item eventKey={planKey} key={planKey}>
                  <Accordion.Header
                    onClick={() => handlePlanClick(plan, planKey)}
                  >
                    <TreatmentAccordionArrow expanded={isPlanExpanded} />
                    <span className="sp-treatment-plan-label">
                      {plan.tp_heading}
                    </span>
                  </Accordion.Header>
                  <Accordion.Body>
                    {isPlanExpanded && (
                      <Accordion
                        activeKey={activePhaseKey}
                        onSelect={(eventKey) => {
                          if (eventKey === null || eventKey === undefined) {
                            setActivePhaseKey(null)
                          } else {
                            setActivePhaseKey(eventKey as string)
                          }
                        }}
                        className="sp-treatment-phase-accordion"
                      >
                        {plan.phases?.map((phase, phaseIndex) => {
                          const phaseKey = getPhaseKey(
                            phase,
                            planIndex,
                            phaseIndex,
                          )
                          const isPhaseExpanded = activePhaseKey === phaseKey

                          return (
                            <Accordion.Item eventKey={phaseKey} key={phaseKey}>
                              <Accordion.Header
                                onClick={() =>
                                  handlePhaseClick(phase, phaseKey)
                                }
                              >
                                <TreatmentAccordionArrow
                                  expanded={isPhaseExpanded}
                                />
                                <div className="sp-treatment-phase-header">
                                  <span className="sp-treatment-phase-name">
                                    {phase.phase_name}
                                  </span>
                                  <div className="sp-treatment-phase-actions">
                                    <EyeViewIcon
                                      className="sp-treatment-phase-view"
                                      aria-hidden
                                    />
                                    <Badge
                                      label={phase.status}
                                      color={getPhaseStatusColor(phase.status)}
                                      mode={badgeMode}
                                      className="sp-treatment-phase-badge"
                                    />
                                  </div>
                                </div>
                              </Accordion.Header>
                              <Accordion.Body>
                                {isPhaseExpanded && (
                                  <div className="sp-treatment-procedure-list">
                                    {(phase.items ?? []).map((item) => (
                                      <PhaseProcedureCard
                                        key={item.item_id}
                                        item={item}
                                        providerName={getProviderName(item)}
                                      />
                                    ))}
                                  </div>
                                )}
                              </Accordion.Body>
                            </Accordion.Item>
                          )
                        })}
                      </Accordion>
                    )}
                  </Accordion.Body>
                </Accordion.Item>
              )
            })}
          </Accordion>
        )}
      </div>
    </div>
  )
}

export default Treatment
